// R5 Packer (IMPLEMENTATION §0.2 R5, GDD §8.2). Family auto-lock (the first item received sets `lockFam`, it
// clears when the Packer is empty); buffer `buffer` (8); one merge action = the lowest identical pair below
// the effective target, costing mergeTicks × workPerL1Tick work with the R2 accumulator (several per tick,
// ≤ maxCompletionsPerTick; no pair = idle = no banked work). Items at or above the target move to `out`
// (≤ outCap) and leave through the front. A full buffer with no pair flushes its lowest item.
// Effective target = min(chosen target, level cap `targetCapByLevel`, the locked family's maximum).
import { packerCap, packerDef, workPerTick } from '../bdefs.ts';
import { dropHeld } from '../containers.ts';
import type { Int } from '../core/types.ts';
import type { Ctx } from '../ctx.ts';
import { discover, newId } from '../entities.ts';
import { clearStall, raiseStall } from '../stall.ts';
import type { ItemRef, PackerB } from '../state.ts';

export function effectiveTarget(c: Ctx, b: PackerB): Int {
  const cap = packerCap(c.d, b.level, b.lockFam);
  return b.target < cap ? b.target : cap;
}

/** Index pair of the lowest identical pair below `limit` (lowest ids first), or null. */
function lowestPair(buf: readonly ItemRef[], limit: Int): [Int, Int] | null {
  let best: [Int, Int] | null = null;
  let bestTier = limit;
  for (let i = 0; i < buf.length; i++) {
    const t = (buf[i] as ItemRef).tier;
    if (t >= bestTier) continue;
    for (let j = i + 1; j < buf.length; j++) {
      if ((buf[j] as ItemRef).tier === t) {
        best = [i, j];
        bestTier = t;
        break;
      }
    }
  }
  return best;
}

/** Moves finished items (tier ≥ target) from the buffer to `out` while it has room. */
function moveReady(c: Ctx, b: PackerB, target: Int): void {
  const cap = c.d.machineRules.outCap;
  for (let i = 0; i < b.buf.length && b.out.length < cap; ) {
    if ((b.buf[i] as ItemRef).tier >= target) b.out.push(b.buf.splice(i, 1)[0] as ItemRef);
    else i++;
  }
}

function mergePair(c: Ctx, b: PackerB, i: Int, j: Int): void {
  const second = b.buf.splice(j, 1)[0] as ItemRef;
  const first = b.buf.splice(i, 1)[0] as ItemRef;
  dropHeld(c, first.id);
  dropHeld(c, second.id);
  const tier = first.tier + 1;
  const isNew = discover(c, first.fam, tier);
  const r: ItemRef = { id: newId(c.s), fam: first.fam, tier };
  b.buf.push(r);
  c.ev.push({
    e: 'merge',
    id: r.id,
    a: first.id,
    b: second.id,
    fam: first.fam,
    tier,
    x: b.x,
    y: b.y,
    first: isNew,
    via: 'machine',
  });
}

/** One tick of Packer work (after its `out` was pushed). Returns true when it worked (busy). */
export function packerStep(c: Ctx, b: PackerB): boolean {
  if (b.buf.length === 0 && b.out.length === 0) b.lockFam = null;
  const target = effectiveTarget(c, b);
  const cost = packerDef(c.d).mergeCost;
  moveReady(c, b, target);
  let pair = lowestPair(b.buf, target);
  if (pair === null) {
    b.prog = 0;
    if (b.buf.length >= packerDef(c.d).buffer) {
      if (b.out.length < c.d.machineRules.outCap) {
        let lo = 0;
        for (let i = 1; i < b.buf.length; i++) {
          if ((b.buf[i] as ItemRef).tier < (b.buf[lo] as ItemRef).tier) lo = i;
        }
        b.out.push(b.buf.splice(lo, 1)[0] as ItemRef);
        clearStall(c, b);
      } else raiseStall(c, b, 'NO_ROOM');
    } else if (b.buf.length > 0 && b.out.length >= c.d.machineRules.outCap) {
      raiseStall(c, b, 'NO_ROOM');
    } else {
      raiseStall(c, b, 'NO_INPUT'); // empty, or waiting for a partner
    }
    return false;
  }
  clearStall(c, b);
  b.prog += workPerTick(c.d, b.level);
  let n = 0;
  while (pair !== null && b.prog >= cost && n < c.d.machineRules.maxCompletionsPerTick) {
    mergePair(c, b, pair[0], pair[1]);
    b.prog -= cost;
    n++;
    moveReady(c, b, target);
    pair = lowestPair(b.buf, target);
  }
  if (pair === null) b.prog = 0;
  return true;
}
